// src/pages/proveedores/ProveedoresPage.js
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Badge, Modal, Form, Row, Col,
  Spinner, Alert
} from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaLink } from 'react-icons/fa';
import {
  listarProveedores,
  crearProveedor,
  actualizarProveedor,
  eliminarProveedor
} from '../../services/proveedores/proveedorService';
import { Alertassw } from '../../components/Alertas/Alertassw';
import Swal from 'sweetalert2';
import BuscadorERPParaNuevoProveedor from '../../components/proveedores/BuscadorERPParaNuevoProveedor';

const ProveedoresPage = () => {
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [proveedorEditando, setProveedorEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  
  // Formulario
  const [formData, setFormData] = useState({
    nombre: '',
    nit: '',
    email: '',
    telefono: '',
    nombreContacto: '',
    ciudad: '',
    direccion: '',
    especialidades: '',
    estado: 'Activo',
    observacion: '',
    idContable: null,
    tipoTercero: 'PROVEEDOR_EXTERNO'
  });

  // Cuando el buscador del ERP encuentra el tercero, autocompletamos el form.
  // No sobreescribimos campos que el usuario ya haya tipeado a mano (excepto
  // nit/idContable que son los que identifican el match con el ERP).
  const handleAutocompletarDesdeERP = (itemERP) => {
    setFormData((prev) => ({
      ...prev,
      nombre: prev.nombre.trim() || itemERP.nombre || '',
      nit: itemERP.nit || prev.nit,
      idContable: itemERP.idContable || null,
      email: prev.email.trim() || itemERP.email || '',
      ciudad: prev.ciudad.trim() || itemERP.ciudad || '',
      direccion: prev.direccion.trim() || itemERP.direccion || '',
      telefono: prev.telefono.trim() || itemERP.telefono || '',
      tipoTercero: 'PROVEEDOR_EXTERNO'
    }));
    Alertassw('success', 'Datos importados', `Se autocompletaron los campos del tercero ${itemERP.nombre}. Revisa y guarda.`);
  };

  useEffect(() => {
    cargarProveedores();
  }, []);

  const cargarProveedores = async () => {
    setLoading(true);
    try {
      const response = await listarProveedores({});
      
      if (response.status === 200 && response.data?.data) {
        setProveedores(response.data.data);
      } else {
        Alertassw('error', 'Error', 'Error al cargar proveedores');
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      Alertassw('error', 'Error', 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleNuevoProveedor = () => {
    setModoEdicion(false);
    setProveedorEditando(null);
    setFormData({
      nombre: '',
      nit: '',
      email: '',
      telefono: '',
      nombreContacto: '',
      ciudad: '',
      direccion: '',
      estado: 'Activo',
      observacion: '',
      idContable: null,
      tipoTercero: 'PROVEEDOR_EXTERNO'
    });
    setShowModal(true);
  };

  const handleEditarProveedor = (proveedor) => {
    setModoEdicion(true);
    setProveedorEditando(proveedor);
    setFormData({
      nombre: proveedor.nombre || '',
      nit: proveedor.nit || '',
      email: proveedor.email || '',
      telefono: proveedor.telefono || '',
      nombreContacto: proveedor.nombreContacto || '',
      ciudad: proveedor.ciudad || '',
      direccion: proveedor.direccion || '',
      estado: proveedor.estado || 'Activo',
      observacion: proveedor.observacion || '',
      idContable: proveedor.idContable || null,
      tipoTercero: proveedor.tipoTercero || 'PROVEEDOR_EXTERNO'
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validarFormulario = () => {
    if (!formData.nombre.trim()) {
      Alertassw('toast', 'Ingrese el nombre del proveedor', '', 'warning');
      return false;
    }

    if (!formData.nit.trim()) {
      Alertassw('toast', 'Ingrese el NIT', '', 'warning');
      return false;
    }

    if (!/^\d+$/.test(formData.nit.trim())) {
      Alertassw('toast', 'El NIT debe contener solo números (sin puntos, guiones ni espacios)', '', 'warning');
      return false;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Alertassw('toast', 'Ingrese un correo electrónico válido', '', 'warning');
      return false;
    }

    return true;
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) return;

    setGuardando(true);
    try {
      let response;

      if (modoEdicion && proveedorEditando) {
        console.log('editando proveedor ID:', proveedorEditando._id, 'con data:', formData);
        response = await actualizarProveedor(proveedorEditando._id, { ...formData });
      } else {
        console.log('creando proveedor con data:', formData);
        response = await crearProveedor({ ...formData });
      }
      if (response.status === 200 || response.status === 201) {
        Alertassw('success', 'Éxito', `Proveedor ${modoEdicion ? 'actualizado' : 'creado'} correctamente`);
        setShowModal(false);
        cargarProveedores();
      } else {
        Alertassw('error', 'Error', response.data?.message || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error al guardar proveedor:', error);
      Alertassw('error', 'Error', 'Error al guardar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  const handleCambiarEstado = async (proveedor) => {
    const nuevoEstado = proveedor.estado === 'Activo' ? 'Inactivo' : 'Activo';
    
    const result = await Swal.fire({
      title: '¿Cambiar estado?',
      text: `¿Desea cambiar el estado a ${nuevoEstado}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await actualizarProveedor(proveedor._id, {
        estado: nuevoEstado
      });

      if (response.status === 200) {
        Alertassw('success', 'Éxito', 'Estado actualizado correctamente');
        cargarProveedores();
      } else {
        Alertassw('error', 'Error', 'Error al actualizar el estado');
      }
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Alertassw('error', 'Error', 'Error al cambiar el estado');
    }
  };

  const handleEliminar = async (proveedor) => {
    const result = await Swal.fire({
      title: '¿Eliminar proveedor?',
      text: `¿Está seguro de eliminar a ${proveedor.nombre}? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await eliminarProveedor(proveedor._id);

      if (response.status === 200) {
        Alertassw('success', 'Éxito', 'Proveedor eliminado correctamente');
        cargarProveedores();
      } else {
        Alertassw('error', 'Error', response.data?.message || response.data?.error || 'Error al eliminar');
      }
    } catch (error) {
      // 409 TIENE_REFERENCIAS: el backend rechaza el delete porque el
      // proveedor aparece en gastos o comprobantes. Ofrecemos inactivar.
      const data = error?.response?.data;
      if (error?.response?.status === 409 && data?.codigo === 'TIENE_REFERENCIAS') {
        const refs = data.referencias || {};
        const detalleHtml = `
          <div class="text-start small">
            <p class="mb-2">${data.error}</p>
            <ul class="mb-3">
              ${refs.gastos > 0 ? `<li><strong>${refs.gastos}</strong> gasto(s) lo referencian</li>` : ''}
              ${refs.comprobantes > 0 ? `<li><strong>${refs.comprobantes}</strong> comprobante(s) contable(s) lo referencian</li>` : ''}
            </ul>
            <p class="mb-0">¿Deseas <strong>inactivarlo</strong> en su lugar? Quedara fuera de los selectores nuevos pero los documentos historicos siguen intactos.</p>
          </div>`;
        const choice = await Swal.fire({
          title: 'No se puede eliminar',
          html: detalleHtml,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, inactivar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#0d6efd'
        });
        if (choice.isConfirmed) {
          try {
            const resp = await actualizarProveedor(proveedor._id, { estado: 'Inactivo' });
            if (resp.status === 200) {
              Alertassw('success', 'Inactivado', `${proveedor.nombre} fue inactivado. Sus referencias historicas se conservan.`);
              cargarProveedores();
            } else {
              Alertassw('error', 'Error', 'No se pudo inactivar el proveedor');
            }
          } catch (e) {
            Alertassw('error', 'Error', 'No se pudo inactivar el proveedor');
          }
        }
        return;
      }

      console.error('Error al eliminar proveedor:', error);
      Alertassw('error', 'Error', data?.error || data?.message || 'Error al eliminar el proveedor');
    }
  };

  return (
    <div className="container-fluid py-4">
      <Card className='w-100'>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center w-100">
          <h5 className="mb-0">
            <i className="bi bi-truck me-2"></i>
            Proveedores
          </h5>
          <Button 
            variant="light" 
            size="sm"
            onClick={handleNuevoProveedor}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Nuevo Proveedor
          </Button>
        </Card.Header>
        
        <Card.Body className='w-100'>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando proveedores...</p>
            </div>
          ) : proveedores.length === 0 ? (
            <Alert variant="info" className="text-center">
              <i className="bi bi-info-circle me-2"></i>
              No hay proveedores registrados
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>NIT</th>
                    <th>Contacto</th>
                    <th className="d-none d-md-table-cell">Ciudad</th>
                    <th className="text-center">SYNC</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {proveedores.map((prov) => (
                    <tr key={prov._id}>
                      <td>
                        <strong>{prov.nombre}</strong>
                      </td>
                      <td>{prov.nit}</td>
                      <td>
                        <small>
                          {prov.nombreContacto}<br />
                          {prov.email}<br />
                          {prov.telefono}
                        </small>
                      </td>
                      <td className="d-none d-md-table-cell">{prov.ciudad}</td>
                      <td className="text-center">
                        {prov.idContable ? (
                          <span title={`Sincronizado con ERP (idContable: ${prov.idContable})`}>
                            <FaCheckCircle className="text-success" size={18} />
                          </span>
                        ) : (
                          <span title="No sincronizado con ERP">
                            <FaTimesCircle className="text-danger" size={18} />
                          </span>
                        )}
                      </td>
                      <td>
                        <Badge bg={prov.estado === 'Activo' ? 'success' : 'secondary'}>
                          {prov.estado}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditarProveedor(prov)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant={prov.estado === 'Activo' ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            onClick={() => handleCambiarEstado(prov)}
                          >
                            <i className={`bi bi-toggle-${prov.estado === 'Activo' ? 'on' : 'off'}`}></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleEliminar(prov)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* MODAL CREAR/EDITAR — responsive: 90vh en desktop, fullscreen en movil */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        fullscreen="md-down"
        scrollable
        centered
        dialogClassName="proveedor-modal-90vh"
      >
        <style>{`
          .proveedor-modal-90vh .modal-content {
            height: 90vh;
            max-height: 90vh;
          }
          @media (max-width: 767.98px) {
            .proveedor-modal-90vh .modal-content {
              height: 100vh;
              max-height: 100vh;
            }
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>
            {modoEdicion ? 'Editar Proveedor' : 'Nuevo Proveedor'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: 'auto' }}>
          {/* Banner / Buscador ERP segun el contexto:
              - Crear: siempre se ofrece sincronizar con el ERP por NIT.
              - Editar + ya sincronizado: banner verde informativo.
              - Editar + no sincronizado: ofrecer sincronizar ahora. */}
          {!modoEdicion && (
            <BuscadorERPParaNuevoProveedor
              onAutocompletar={handleAutocompletarDesdeERP}
              disabled={guardando}
            />
          )}
          {modoEdicion && formData.idContable && (
            <Alert variant="success" className="d-flex align-items-center mb-3">
              <FaLink className="me-2" />
              <div>
                <strong>Sincronizado con software contable.</strong>{' '}
                <small className="d-block text-muted">
                  idContable: <code>{formData.idContable}</code>
                </small>
              </div>
            </Alert>
          )}
          {modoEdicion && !formData.idContable && (
            <BuscadorERPParaNuevoProveedor
              onAutocompletar={handleAutocompletarDesdeERP}
              disabled={guardando}
              nitInicial={formData.nit}
            />
          )}
          <Form>
            <Row className="g-3">
              <Col xs={12} md={8}>
                <Form.Group>
                  <Form.Label>
                    Nombre <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Nombre de la empresa"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>
                    NIT <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nit"
                    value={formData.nit}
                    onChange={handleChange}
                    placeholder="123456789-0"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contacto@proveedor.com"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="text"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="3001234567"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Nombre Contacto</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombreContacto"
                    value={formData.nombreContacto}
                    onChange={handleChange}
                    placeholder="Juan Pérez"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Ciudad</Form.Label>
                  <Form.Control
                    type="text"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                    placeholder="Bogotá"
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Calle 123 #45-67"
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                  >
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="observacion"
                    value={formData.observacion}
                    onChange={handleChange}
                    placeholder="Observaciones adicionales..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGuardar}
            disabled={guardando}
          >
            {guardando ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Guardando...
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i>
                {modoEdicion ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProveedoresPage;
